import { useMemo } from "react";
import { useController, useFormContext } from "react-hook-form";
import { Label } from "@src/common/components";
import { DispatcherOrderEntryAdditionalField } from "@api/graphql/generated/generated-types";
import { useAuth } from "@src/common/hooks/useAuth";
import { useIsCallerNameRequired } from "@src/common/hooks/useIsCallerNameRequired";
import { useIsCallerPhoneRequired } from "@src/common/hooks/useIsCallerPhoneRequired";
import {
  isCallerRequired,
  isCallerPhoneRequired,
} from "@src/common/lib/requiredAdditionalFieldUtils";
import { useEndCustomerById } from "@src/common/hooks/useEndCustomerById";
import { appendUniqueEmailRecipients } from "@src/common/lib/emailUtils";
import CallerContactInput from "@src/common/components/CallerContactInput";
import { CallerContactOption } from "@src/common/components/CallerContactInput/useCallerContactSearch";
import CallerPhoneInput from "@src/common/components/CallerPhoneInput";
import { CallerPhoneOption } from "@src/common/components/CallerPhoneInput/useCallerPhoneSearch";
import { useCreateOrderContext } from "../../../utils/CreateOrderContextProvider";

// Caller Name is required when the tenant config says so, or whenever a phone
// is entered (a phone with no name to attribute it to isn't allowed).
export function validateCallerName(fields: DispatcherOrderEntryAdditionalField[]) {
  return (caller: string | undefined, formValues: { callerPhone?: string }) =>
    !(isCallerRequired(fields) || !!formValues.callerPhone) ||
    !!caller?.trim() ||
    "Caller Name is required";
}

export function validateCallerPhone(fields: DispatcherOrderEntryAdditionalField[]) {
  return (callerPhone: string | undefined) =>
    !isCallerPhoneRequired(fields) ||
    !!callerPhone?.trim() ||
    "Caller Phone is required";
}

interface RequiredLabelProps {
  text: string;
  required: boolean;
}

function RequiredLabel({ text, required }: RequiredLabelProps) {
  return (
    <Label className="block text-xs font-medium text-gray-700 dark:text-gray-300 truncate">
      {text}
      {required ? (
        <span className="text-red-500"> *</span>
      ) : (
        <span className="text-slate-400 dark:text-gray-500 text-xs"> (Optional)</span>
      )}
    </Label>
  );
}

/**
 * Caller Name + Caller Phone for order entry. Both are registered with RHF so
 * handleSubmit validates them like any other field; required-ness comes from
 * the customer's dispatcherOrderEntryAdditionalFields, falling back to the
 * courier's.
 */
export function CallerFields() {
  const { courier } = useAuth();
  const { selectedCustomer, setSelectedCustomer } = useCreateOrderContext();
  const {
    control,
    formState: { errors },
    setValue,
    getValues,
    clearErrors,
  } = useFormContext();
  const { fetchEndCustomer } = useEndCustomerById();

  const dispatcherOrderEntryAdditionalFields = useMemo(() => {
    return (
      selectedCustomer?.dispatcherOrderEntryAdditionalFields ||
      courier?.courier?.dispatcherOrderEntryAdditionalFields ||
      []
    );
  }, [selectedCustomer, courier]);

  const isCallerNameRequired = useIsCallerNameRequired(dispatcherOrderEntryAdditionalFields);
  const callerPhoneRequired = useIsCallerPhoneRequired(dispatcherOrderEntryAdditionalFields);

  const { field: callerField } = useController({
    name: "caller",
    control,
    rules: { validate: validateCallerName(dispatcherOrderEntryAdditionalFields) },
  });
  const { field: callerPhoneField } = useController({
    name: "callerPhone",
    control,
    rules: {
      validate: validateCallerPhone(dispatcherOrderEntryAdditionalFields),
      // Caller Name's rule depends on the phone, so re-check it on phone edits.
      deps: "caller",
    },
  });

  // Picking a saved contact from either input fills both fields and appends
  // the contact's emails. It only fills the customer when none is selected
  // yet; a selected End Customer never changes based on the Caller (CRI-185).
  const applyCallerContact = (contact: CallerContactOption | CallerPhoneOption) => {
    if (contact.name) {
      callerField.onChange(contact.name);
    }
    if (contact.phone) {
      callerPhoneField.onChange(contact.phone);
    }
    if (contact.emails) {
      const currentEmails: string = getValues("additionalEmailRecipients") || "";
      const updatedEmails = appendUniqueEmailRecipients(currentEmails, contact.emails);
      if (updatedEmails !== currentEmails) {
        setValue("additionalEmailRecipients", updatedEmails);
      }
    }
    if (!selectedCustomer) {
      fetchEndCustomer(contact.endCustomerId).then((customer) => {
        if (customer) {
          setValue("endCustomerId", customer.id);
          setSelectedCustomer(customer);
          clearErrors("endCustomerId");
        }
      });
    }
  };

  return (
    <>
      <div className="col-span-4">
        <div className="flex flex-col justify-between">
          <RequiredLabel text="Caller Name" required={isCallerNameRequired} />
          <CallerContactInput
            value={callerField.value || ""}
            selectedCustomer={selectedCustomer}
            required={isCallerNameRequired}
            error={!!errors.caller}
            onSelect={applyCallerContact}
            onChange={callerField.onChange}
          />
        </div>
      </div>
      <div className="col-span-4">
        <div className="flex flex-col justify-between">
          <RequiredLabel text="Caller Phone" required={callerPhoneRequired} />
          <CallerPhoneInput
            value={callerPhoneField.value || ""}
            selectedCustomer={selectedCustomer}
            error={!!errors.callerPhone}
            onSelect={applyCallerContact}
            onChange={callerPhoneField.onChange}
          />
        </div>
      </div>
    </>
  );
}
